/**
 * Language display utilities for BCP 47 language tags.
 *
 * Maps language codes to human-readable labels with flag emojis.
 * Used by LanguageValueLabel and LanguageInput components.
 */

export interface LanguageOption {
  /** BCP 47 language tag (e.g., "en-US", "fr") */
  code: string;
  /** Human-readable label (e.g., "English (US)") */
  label: string;
  /** Flag emoji for the primary region */
  flag: string;
}

/**
 * Common languages with their BCP 47 codes, display labels, and flag emojis.
 * Ordered roughly by global internet usage.
 */
export const COMMON_LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'en-US', label: 'English (US)', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: 'en-GB', label: 'English (UK)', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'en-AU', label: 'English (Australia)', flag: '\u{1F1E6}\u{1F1FA}' },
  { code: 'en-CA', label: 'English (Canada)', flag: '\u{1F1E8}\u{1F1E6}' },
  { code: 'en-IE', label: 'English (Ireland)', flag: '\u{1F1EE}\u{1F1EA}' },
  { code: 'en-NZ', label: 'English (New Zealand)', flag: '\u{1F1F3}\u{1F1FF}' },
  { code: 'zh', label: 'Chinese', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: 'zh-CN', label: 'Chinese (Simplified)', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: 'zh-TW', label: 'Chinese (Traditional)', flag: '\u{1F1F9}\u{1F1FC}' },
  { code: 'zh-HK', label: 'Chinese (Hong Kong)', flag: '\u{1F1ED}\u{1F1F0}' },
  { code: 'es', label: 'Spanish', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'es-MX', label: 'Spanish (Mexico)', flag: '\u{1F1F2}\u{1F1FD}' },
  { code: 'es-AR', label: 'Spanish (Argentina)', flag: '\u{1F1E6}\u{1F1F7}' },
  { code: 'fr', label: 'French', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'fr-CA', label: 'French (Canada)', flag: '\u{1F1E8}\u{1F1E6}' },
  { code: 'fr-BE', label: 'French (Belgium)', flag: '\u{1F1E7}\u{1F1EA}' },
  { code: 'fr-CH', label: 'French (Switzerland)', flag: '\u{1F1E8}\u{1F1ED}' },
  { code: 'de', label: 'German', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'de-AT', label: 'German (Austria)', flag: '\u{1F1E6}\u{1F1F9}' },
  { code: 'de-CH', label: 'German (Switzerland)', flag: '\u{1F1E8}\u{1F1ED}' },
  { code: 'pt', label: 'Portuguese', flag: '\u{1F1F5}\u{1F1F9}' },
  { code: 'pt-BR', label: 'Portuguese (Brazil)', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: 'ja', label: 'Japanese', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: 'ko', label: 'Korean', flag: '\u{1F1F0}\u{1F1F7}' },
  { code: 'it', label: 'Italian', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: 'nl', label: 'Dutch', flag: '\u{1F1F3}\u{1F1F1}' },
  { code: 'nl-BE', label: 'Dutch (Belgium)', flag: '\u{1F1E7}\u{1F1EA}' },
  { code: 'ru', label: 'Russian', flag: '\u{1F1F7}\u{1F1FA}' },
  { code: 'ar', label: 'Arabic', flag: '\u{1F1F8}\u{1F1E6}' },
  { code: 'hi', label: 'Hindi', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: 'bn', label: 'Bengali', flag: '\u{1F1E7}\u{1F1E9}' },
  { code: 'pl', label: 'Polish', flag: '\u{1F1F5}\u{1F1F1}' },
  { code: 'tr', label: 'Turkish', flag: '\u{1F1F9}\u{1F1F7}' },
  { code: 'vi', label: 'Vietnamese', flag: '\u{1F1FB}\u{1F1F3}' },
  { code: 'th', label: 'Thai', flag: '\u{1F1F9}\u{1F1ED}' },
  { code: 'uk', label: 'Ukrainian', flag: '\u{1F1FA}\u{1F1E6}' },
  { code: 'cs', label: 'Czech', flag: '\u{1F1E8}\u{1F1FF}' },
  { code: 'sv', label: 'Swedish', flag: '\u{1F1F8}\u{1F1EA}' },
  { code: 'da', label: 'Danish', flag: '\u{1F1E9}\u{1F1F0}' },
  { code: 'fi', label: 'Finnish', flag: '\u{1F1EB}\u{1F1EE}' },
  { code: 'no', label: 'Norwegian', flag: '\u{1F1F3}\u{1F1F4}' },
  { code: 'nb', label: 'Norwegian Bokm\u00E5l', flag: '\u{1F1F3}\u{1F1F4}' },
  { code: 'el', label: 'Greek', flag: '\u{1F1EC}\u{1F1F7}' },
  { code: 'he', label: 'Hebrew', flag: '\u{1F1EE}\u{1F1F1}' },
  { code: 'ro', label: 'Romanian', flag: '\u{1F1F7}\u{1F1F4}' },
  { code: 'hu', label: 'Hungarian', flag: '\u{1F1ED}\u{1F1FA}' },
  { code: 'id', label: 'Indonesian', flag: '\u{1F1EE}\u{1F1E9}' },
  { code: 'ms', label: 'Malay', flag: '\u{1F1F2}\u{1F1FE}' },
  { code: 'sk', label: 'Slovak', flag: '\u{1F1F8}\u{1F1F0}' },
  { code: 'bg', label: 'Bulgarian', flag: '\u{1F1E7}\u{1F1EC}' },
  { code: 'hr', label: 'Croatian', flag: '\u{1F1ED}\u{1F1F7}' },
  { code: 'sr', label: 'Serbian', flag: '\u{1F1F7}\u{1F1F8}' },
  { code: 'ca', label: 'Catalan', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'fil', label: 'Filipino', flag: '\u{1F1F5}\u{1F1ED}' },
  { code: 'sw', label: 'Swahili', flag: '\u{1F1F0}\u{1F1EA}' },
];

/** Lookup map for O(1) access by code (case-insensitive). */
const LANGUAGE_MAP = new Map<string, LanguageOption>(
  COMMON_LANGUAGES.map(lang => [lang.code.toLowerCase(), lang])
);

/**
 * Get the display label for a BCP 47 language code.
 * Falls back to the raw code if not in the known list.
 */
export function getLanguageLabel(code: string): string {
  const match = LANGUAGE_MAP.get(code.toLowerCase());
  if (match) {
    return match.label;
  }

  // Try matching just the base language (e.g., "en-ZA" → "en" → "English")
  const baseLang = code.split('-')[0].toLowerCase();
  const baseMatch = LANGUAGE_MAP.get(baseLang);
  if (baseMatch) {
    return baseMatch.label;
  }

  return code;
}

/**
 * Get the flag emoji for a BCP 47 language code.
 * Returns empty string if not in the known list.
 */
export function getLanguageFlag(code: string): string {
  const match = LANGUAGE_MAP.get(code.toLowerCase());
  if (match) {
    return match.flag;
  }

  // Try matching just the base language
  const baseLang = code.split('-')[0].toLowerCase();
  const baseMatch = LANGUAGE_MAP.get(baseLang);
  if (baseMatch) {
    return baseMatch.flag;
  }

  // Try to derive flag from region subtag (e.g., "en-ZA" → ZA → flag)
  const parts = code.split('-');
  if (parts.length >= 2) {
    const region = parts[parts.length - 1].toUpperCase();
    if (region.length === 2) {
      return regionToFlag(region);
    }
  }

  return '';
}

/**
 * Resolve a BCP 47 code to the canonical option id used in COMMON_LANGUAGES.
 *
 * Matching is case-insensitive so stored values like "EN-US" or "en-us" still
 * select their option in pickers. Falls back to the base language tag, then
 * returns the input unchanged if nothing matches.
 */
export function getCanonicalLanguageCode(code: string): string {
  const match = LANGUAGE_MAP.get(code.toLowerCase());
  if (match) {
    return match.code;
  }

  const baseLang = code.split('-')[0].toLowerCase();
  const baseMatch = LANGUAGE_MAP.get(baseLang);
  if (baseMatch) {
    return baseMatch.code;
  }

  return code;
}

/**
 * Get formatted display string: "flag label" (e.g., "🇫🇷 French")
 */
export function getLanguageDisplay(code: string): string {
  const flag = getLanguageFlag(code);
  const label = getLanguageLabel(code);
  return flag ? `${flag} ${label}` : label;
}

/** Convert a 2-letter ISO region code to a flag emoji. */
function regionToFlag(region: string): string {
  const codePoints = [...region.toUpperCase()].map(
    char => 0x1f1e6 + char.charCodeAt(0) - 65
  );
  return String.fromCodePoint(...codePoints);
}
